/**
 * 按设备抽「真实点位」目录 + metricKey 契约（docs/05）。
 *
 * ## 契约（唯一真相）
 * - attr1 里：`key` = 点号（D9119），`name` = 中文属性名（进给率）
 * - 场景绑定 / 信息卡 / liveData metrics 键：一律用 **中文属性名**（metricKey）
 * - 点号只出现在 `sourceShape.fieldMappings[].sourcePath`（如 attr1.D9119.value）
 * - 信息卡 DOM 显示的是 `infoCard.fields[].key`，因此 key 必须是中文名，不能是 D9119
 *
 * ## 历史坑
 * Agent / 旧逻辑曾把点号写成 metricKey → 卡片显示 D9119、运行时取不到数（metrics 键是中文）。
 * 本模块集中：发现点位、规范化 metrics、修复已错绑对象；禁止各写盘路径各自为政。
 */
import type { SelectedMetricEntry } from '@/types/connectData'
import { buildLiveDataKey } from '@/types/connectData'
import type { SourceShape } from '@/types/sourceAdapter'
import type { SceneData, SceneObject } from '@/types/scene'
import { previewDevicesFromRaw } from '@/runtime/realtime/sourceAdapterDesign'

/** 单台设备上一个点位的展示信息（key=attr1 点号，name=中文名） */
export interface DevicePointInfo {
  key: string
  name: string
  value?: string
  unit?: string
}

/** 带真实点位目录的设备行（替代旧的 {assetCode,name}） */
export interface EnrichedDevice {
  assetCode: string
  name: string
  /** identity.deviceTypeSourcePath 的原始值；未配置时为空，映射登记将按设备号匹配 */
  deviceTypeValue?: string
  points: DevicePointInfo[]
}

/**
 * 信息卡默认最多展示的点位数（用户拍板：卡片字段最多 5 个）。
 * 抽屉「卡片字段」勾选与 Agent 默认选择都以它为上限。
 */
export const MAX_CARD_FIELDS = 5

/** PLC/MES 点号形态（如 D9119、d8242）；禁止作为 metricKey / 信息卡 key */
export function isPlcPointCode(s: string): boolean {
  return /^D\d+$/i.test(String(s ?? '').trim())
}

/** 可用作 metricKey 的中文（或其它）属性名：非空且不是点号 */
export function isUsableMetricName(name: string): boolean {
  const t = String(name ?? '').trim()
  return t.length > 0 && !isPlcPointCode(t)
}

/**
 * 从整包返回抽每台设备的真实点位目录。
 * 丢弃 name 为空或 name 本身是点号的条目（无法作为合法 metricKey）。
 */
export function enrichDevicesWithPoints(raw: unknown, sourceShape: SourceShape): EnrichedDevice[] {
  const previews = previewDevicesFromRaw(raw, sourceShape)
  return previews
    .filter((p) => p.assetCode)
    .map((p) => ({
      assetCode: p.assetCode,
      name: p.deviceName && String(p.deviceName).trim() ? String(p.deviceName) : p.assetCode,
      // 只用 preview 的 deviceTypeValue（真类型）；绝不用 deviceTypeKey（可能是点位指纹）
      ...(p.deviceTypeValue ? { deviceTypeValue: p.deviceTypeValue } : {}),
      points: p.points
        .filter((pt) => isUsableMetricName(pt.name))
        .map((pt) => {
          const info: DevicePointInfo = { key: pt.key, name: pt.name.trim(), value: pt.value }
          return info
        }),
    }))
}

/** 在缓存或缺省设备表里按编号找带点位的设备 */
export function deviceByCode(devices: EnrichedDevice[] | undefined, assetCode: string): EnrichedDevice | undefined {
  return devices?.find((d) => d.assetCode === assetCode)
}

/**
 * 把模型/旧数据可能误传的「点号 D9119」规范为中文属性名。
 * 无点位目录时：直接丢弃纯点号，绝不放行进卡片。
 */
export function normalizeMetricsToChineseNames(
  metrics: SelectedMetricEntry[],
  points: DevicePointInfo[] | undefined,
): SelectedMetricEntry[] {
  if (!metrics.length) return metrics
  if (!points?.length) {
    return metrics.filter((m) => isUsableMetricName(m.metricKey))
  }
  const byKey = new Map(points.map((p) => [p.key, p]))
  const byName = new Map(points.map((p) => [p.name, p]))
  const out: SelectedMetricEntry[] = []
  const seen = new Set<string>()
  for (const m of metrics) {
    if (out.length >= MAX_CARD_FIELDS) break
    const raw = String(m.metricKey ?? '').trim()
    if (!raw) continue
    const pt = byName.get(raw) ?? byKey.get(raw)
    if (!pt || !isUsableMetricName(pt.name)) continue
    if (seen.has(pt.name)) continue
    seen.add(pt.name)
    const displayRaw = String(m.displayName ?? '').trim()
    const display =
      displayRaw && displayRaw !== raw && isUsableMetricName(displayRaw) ? displayRaw : pt.name
    out.push({
      metricKey: pt.name,
      displayName: display,
      ...(m.unit || pt.unit ? { unit: m.unit ?? pt.unit } : {}),
    })
  }
  return out
}

/** 从真实点位目录生成默认卡片指标（前 MAX_CARD_FIELDS 个，metricKey=中文名） */
export function metricsFromDevicePoints(points: DevicePointInfo[]): SelectedMetricEntry[] {
  const seen = new Set<string>()
  const out: SelectedMetricEntry[] = []
  for (const pt of points) {
    if (!isUsableMetricName(pt.name) || seen.has(pt.name) || out.length >= MAX_CARD_FIELDS) continue
    seen.add(pt.name)
    out.push({
      metricKey: pt.name,
      displayName: pt.name,
      ...(pt.unit ? { unit: pt.unit } : {}),
    })
  }
  return out
}

/**
 * 修复单个对象：把 infoCard / dataBindings 里误用的点号 metricKey 改成中文名，并重写 dataKey。
 * 已是中文名且能在点位目录中找到的保持不变。
 */
export function repairObjectBindingsToChineseNames(
  obj: SceneObject,
  serviceId: string,
  points: DevicePointInfo[],
): {
  object: SceneObject
  changed: boolean
  selectedPoints: Array<{ key: string; name: string; unit?: string }>
} {
  if (!points.length || !obj.assetCode) {
    return { object: obj, changed: false, selectedPoints: [] }
  }
  const fields = obj.dataBindings?.infoCard?.fields ?? []
  if (!fields.length && !(obj.infoCard?.fields?.length)) {
    return { object: obj, changed: false, selectedPoints: [] }
  }

  const byKey = new Map(points.map((p) => [p.key, p]))
  const byName = new Map(points.map((p) => [p.name, p]))
  const metrics: SelectedMetricEntry[] = []
  const selectedPoints: Array<{ key: string; name: string; unit?: string }> = []
  const seen = new Set<string>()
  let needsRepair = false

  const rawKeys =
    fields.length > 0
      ? fields.map((f) => ({
          raw: String(f.metricKey || f.fieldKey || '').trim(),
          label: f.label,
          unit: f.unit,
        }))
      : (obj.infoCard?.fields ?? []).map((f) => ({
          raw: String(f.key ?? '').trim(),
          label: undefined as string | undefined,
          unit: undefined as string | undefined,
        }))

  for (const row of rawKeys) {
    if (!row.raw || metrics.length >= MAX_CARD_FIELDS) continue
    const pt = byName.get(row.raw) ?? byKey.get(row.raw)
    if (!pt || !isUsableMetricName(pt.name)) {
      if (isPlcPointCode(row.raw)) needsRepair = true
      continue
    }
    if (isPlcPointCode(row.raw) || row.raw !== pt.name) needsRepair = true
    if (seen.has(pt.name)) continue
    seen.add(pt.name)
    const display =
      row.label && isUsableMetricName(row.label) && row.label !== row.raw ? row.label : pt.name
    metrics.push({
      metricKey: pt.name,
      displayName: display,
      ...(row.unit || pt.unit ? { unit: row.unit ?? pt.unit } : {}),
    })
    selectedPoints.push({ key: pt.key, name: pt.name, ...(pt.unit ? { unit: pt.unit } : {}) })
  }

  if (!needsRepair && metrics.length === fields.length) {
    // 再扫一眼 infoCard.key 是否仍有点号
    const cardHasCode = (obj.infoCard?.fields ?? []).some((f) => isPlcPointCode(f.key))
    if (!cardHasCode) return { object: obj, changed: false, selectedPoints }
  }

  const infoCard = {
    enabled: metrics.length > 0,
    fields: metrics.map((m) => ({
      key: m.metricKey,
      value: obj.infoCard?.fields?.find((f) => f.key === m.metricKey || byKey.get(f.key)?.name === m.metricKey)?.value ?? '--',
    })),
  }
  const dataBindings = {
    ...(obj.dataBindings ?? {}),
    infoCard: {
      fields: metrics.map((m) => ({
        fieldKey: m.metricKey,
        label: m.displayName,
        dataKey: buildLiveDataKey(serviceId, obj.assetCode!, m.metricKey),
        metricKey: m.metricKey,
        unit: m.unit,
      })),
    },
  }
  return {
    object: { ...obj, dataServiceId: serviceId, infoCard, dataBindings },
    changed: true,
    selectedPoints,
  }
}

export interface RepairSceneBindingsResult {
  scene: SceneData
  repairedObjectIds: string[]
  /** 供调用方 ensureDeviceFieldMappings 登记映射 */
  mappingJobs: Array<{
    assetCode: string
    deviceTypeValue?: string
    points: Array<{ key: string; name: string; unit?: string }>
  }>
}

/**
 * 按服务试拉缓存，修复场景中所有绑到该服务且误用点号的对象。
 * fieldMappings 登记由调用方用 mappingJobs + ensureDeviceFieldMappings 完成（避免循环依赖）。
 */
export function repairSceneBindingsForService(
  scene: SceneData,
  serviceId: string,
  devices: EnrichedDevice[],
): RepairSceneBindingsResult {
  const byCode = new Map(devices.map((d) => [d.assetCode, d]))
  const objects = scene.objects.map((o) => ({ ...o }))
  const repairedObjectIds: string[] = []
  const mappingJobs: RepairSceneBindingsResult['mappingJobs'] = []

  for (let i = 0; i < objects.length; i++) {
    const obj = objects[i]!
    if (obj.dataServiceId !== serviceId || !obj.assetCode) continue
    const dev = byCode.get(obj.assetCode)
    if (!dev?.points?.length) continue
    const { object: next, changed, selectedPoints } = repairObjectBindingsToChineseNames(
      obj,
      serviceId,
      dev.points,
    )
    if (!changed) continue
    objects[i] = next
    repairedObjectIds.push(next.id)
    if (selectedPoints.length) {
      mappingJobs.push({
        assetCode: next.assetCode!,
        ...(dev.deviceTypeValue ? { deviceTypeValue: dev.deviceTypeValue } : {}),
        points: selectedPoints,
      })
    }
  }

  if (!repairedObjectIds.length) {
    return { scene, repairedObjectIds, mappingJobs: [] }
  }
  return {
    scene: { ...scene, objects },
    repairedObjectIds,
    mappingJobs,
  }
}
